import type { PortInfo } from "@/types/booking";
import { formatBookingDate, formatUsd } from "@/types/booking";
import type { DisplayPricingMeta } from "@/types/pricingTier";
import type {
  BookingPolicies,
  DndPolicy,
  OverweightPolicy,
  RateCardProvenance,
  RateLineDdPolicy,
  SurchargeNoticeOverlay,
  WeightLimitPolicy,
} from "@/types/api";

/**
 * Prepaid / Collect / Spot / LCL classification. For FCL families this is
 * derived from the raw card `source` string plus `is_spot` (see
 * `classifyRateSource`); `lcl` is set by the backend for LCL agent
 * consolidation tariffs (carrier `LCL`, source `agent:oceanlink`), priced per
 * W/M revenue ton rather than per container.
 */
export type RateSourceType = "collect" | "prepaid" | "spot" | "lcl";

/** Overlap-aware card status computed by the backend. */
export type RateCardStatus = "active" | "partial" | "superseded" | "expired";

export interface RateSurcharge {
  code: string;
  amount: number;
  currency: string;
  basis: string;
  category?: string | null;
}

/**
 * Contract-level row: one current rate card for a
 * (carrier, contract_ref, source_type) identity. Powers the list and the
 * detail hero.
 */
export interface RateCardRow {
  id: string;
  carrier: string;
  contract_ref: string | null;
  contract_revision: string | null;
  named_account: string | null;
  source: string;
  source_type: RateSourceType;
  status: RateCardStatus;
  valid_from: string | null;
  valid_to: string | null;
  line_count: number;
  avg_frt: number;
  avg_price_net: number | null;
  is_spot: boolean;
  email_date?: string | null;
  created_at: string;
  batch_id?: string | null;
  batch_attachment_id?: string | null;
  provenance?: RateCardProvenance | null;
  overweight_policies: OverweightPolicy[];
  weight_limit_policies: WeightLimitPolicy[];
  booking_policies: BookingPolicies | null;
  dnd_policies: DndPolicy[];
}

/** A single pricing line within a card, shown in the detail lines table. */
export interface RateLineRow {
  id: string;
  rate_card_id: string;
  carrier: string;
  contract_ref: string | null;
  origin_port: PortInfo;
  destination_port: PortInfo;
  equipment_size: string;
  equipment_type: string;
  equipment_summary: string;
  is_hazardous: boolean;
  currency: string;
  frt: number;
  /**
   * FRT++ compound rate: FRT + per-container same-currency surcharges,
   * excluding B/L fees and terminal handling (THC/THD). Prefers a notice
   * overlay when the API sent one.
   */
  frt_pp: number;
  /** price_net when present, otherwise falls back to FRT. */
  all_in: number;
  valid_from: string | null;
  valid_to: string | null;
  is_current: boolean;
  version: number;
  /** Raw surcharges from the rate card (before active surcharge notices). */
  surcharges: RateSurcharge[];
  /** Per-code adjustments from active surcharge notices (when any apply). */
  notice_overlays?: SurchargeNoticeOverlay[];
  /** Surcharges after applying active surcharge notices (when any apply). */
  surcharges_overlaid?: RateSurcharge[] | null;
  max_weight: number | null;
  max_gross_kg: number | null;
  max_cargo_kg: number | null;
  max_weight_basis: string | null;
  weight_limit_unit: string | null;
  free_time_days: number | null;
  free_time_days_max: number | null;
  dd_policies: RateLineDdPolicy[];
  /** Inland feeder POL when this line is via pre-carriage. */
  pre_carriage_pol: PortInfo | null;
  /** Mode of transport for the pre-carriage leg (e.g. Barge, Rail). */
  pre_carriage_mot: string | null;
  /** FCL (per-container) vs LCL (per W/M revenue ton) pricing basis. */
  load_type?: string | null;
  /** Freight basis label for LCL lines (e.g. "W/M" = weight-or-measure). */
  freight_basis?: string | null;
  /** LCL consolidation / handling agent at destination. */
  handling_agent?: string | null;
  /** LCL routing note (e.g. transshipment hub). */
  route_via?: string | null;
  /**
   * Card-level policies attached when the line is opened in the rail so the
   * surcharge panel can show the weight / booking rules that apply to it.
   */
  cardPolicies?: RateCardPoliciesSnapshot;
  /**
   * Pricing-tier display metadata when a non-full tier applied for the viewer.
   * For `quote` the tier buffer is on base `frt` only; `frt_pp` / `all_in` are
   * recomputed from surcharges; `hidden` / `client_invoice` null the figures.
   * Absent for full-tier viewers / admins.
   */
  display_pricing?: DisplayPricingMeta | null;
}

/** Card-level policy snapshot passed into the selected-line rail panel. */
export interface RateCardPoliciesSnapshot {
  overweight_policies: OverweightPolicy[];
  weight_limit_policies: WeightLimitPolicy[];
  booking_policies: BookingPolicies | null;
  dnd_policies: DndPolicy[];
}

export const RATE_SOURCE_TYPE_LABELS: Record<RateSourceType, string> = {
  collect: "Collect",
  prepaid: "Prepaid",
  spot: "Spot",
  lcl: "LCL",
};

export const RATE_STATUS_LABELS: Record<RateCardStatus, string> = {
  active: "Active",
  partial: "Partial",
  superseded: "Superseded",
  expired: "Expired",
};

/** Default rate-cards list filter: in-effect cards (active + partial overlap). */
export const DEFAULT_RATE_CARDS_LIST_STATUS = "current";

export type RateSourceTypeFilter = RateSourceType | "all";

/** Source-type tabs shown in the rates header filter. */
export const RATE_SOURCE_FILTER_OPTIONS: {
  value: RateSourceTypeFilter;
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "collect", label: "Collect" },
  { value: "prepaid", label: "Prepaid" },
  { value: "spot", label: "Spot" },
  { value: "lcl", label: "LCL" },
];

/** Translate a source-type filter tab into the API `source_type` query value. */
export function sourceTypeFilterToApiParam(
  filter: RateSourceTypeFilter,
): string | undefined {
  return filter === "all" ? undefined : filter;
}

const CARRIER_DIRECT_SOURCES = new Set([
  "zim",
  "msc",
  "one",
  "emc",
  "yml",
  "maersk",
  "msk",
  "hapag",
  "hpl",
  "cosco",
  "cma",
  "hmm",
  "oocl",
]);

/**
 * Derive the prepaid/collect/spot classification from a raw card source and
 * its spot flag — mirrors the backend `classify_source_type` logic.
 */
export function classifyRateSource(
  source: string | null | undefined,
  isSpot?: boolean,
): RateSourceType {
  if (isSpot) return "spot";
  if (!source) return "spot";
  const s = source.trim().toLowerCase();
  if (s.startsWith("agent:")) return "prepaid";
  if (s === "manual" || s === "api") return "spot";
  if (CARRIER_DIRECT_SOURCES.has(s)) return "collect";
  return "collect";
}

/** Coerce a raw status string into the known `RateCardStatus` union. */
export function normalizeRateStatus(status: string | null | undefined): RateCardStatus {
  if (
    status === "active" ||
    status === "partial" ||
    status === "superseded" ||
    status === "expired"
  ) {
    return status;
  }
  return "active";
}

export function routeLabel(
  rate: Pick<RateLineRow, "origin_port" | "destination_port">,
): string {
  return `${rate.origin_port.locode} → ${rate.destination_port.locode}`;
}

export function equipmentLabel(
  rate: Pick<RateLineRow, "equipment_size" | "equipment_type">,
): string {
  return `${rate.equipment_size}' ${rate.equipment_type}`;
}

export { formatBookingDate as formatRateDate, formatUsd };
